import clsx from 'clsx';
import { memo, ReactNode } from 'react';
import { Box, Flex } from '@optiaxiom/react';
import { Handle, Position, useConnection } from '@xyflow/react';

import {
  ExecutionStatus,
  leftCircle,
  nodeContainer,
  nodeContainerModifiers,
  rightCircle,
  sourceHandle,
  targetHandle,
  targetHandleConnecting,
  targetHandleError,
} from './NodeContainer.css';

type NodeContainerProps = {
  children: ReactNode;
  selected?: boolean;
  hasErrors?: boolean;
  isSourceNode?: boolean;
  executionStatus?: ExecutionStatus;
  isExecutionSelected?: boolean;
  style?: React.CSSProperties;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  className?: string;
  dataAttributes?: Record<string, string | boolean>;
  nodeId?: string;
};

export const NodeContainer = memo(
  ({
    children,
    selected,
    hasErrors,
    isSourceNode,
    executionStatus,
    isExecutionSelected,
    style,
    onMouseEnter,
    onMouseLeave,
    className,
    dataAttributes,
    nodeId,
  }: NodeContainerProps) => {
    const connection = useConnection();
    const isTarget = connection.inProgress && connection.toNode?.id === nodeId;
    const isNonSource = connection.inProgress && connection.fromNode?.id !== nodeId;

    const isConnecting = connection.inProgress;
    const isValidConnection = connection.isValid;
    const showError = isTarget && !isValidConnection;

    // Convert dataAttributes to data-* props
    const dataProps = dataAttributes
      ? Object.entries(dataAttributes).reduce(
          (acc, [key, value]) => {
            acc[`data-${key}`] = String(value);
            return acc;
          },
          {} as Record<string, string>
        )
      : {};

    return (
      <Flex
        className={clsx(
          nodeContainer,
          selected && nodeContainerModifiers.selected,
          hasErrors && nodeContainerModifiers.error,
          isSourceNode && nodeContainerModifiers.source,
          executionStatus && nodeContainerModifiers[executionStatus],
          isExecutionSelected && nodeContainerModifiers.executionSelected,
          className
        )}
        style={style}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...dataProps}
      >
        {/* Connection circles on left and right edges */}
        <Box className={leftCircle} />
        <Box className={rightCircle} />

        {/* Source handle - entire node body, hidden during connection */}
        {!isConnecting && <Handle type="source" position={Position.Top} className={sourceHandle} />}

        {/* Target handle - entire node body, shown when not connecting or when this is the target */}
        {(!isConnecting || isNonSource) && (
          <Handle
            type="target"
            position={Position.Top}
            className={clsx(
              targetHandle,
              showError && targetHandleError,
              isTarget && !showError && targetHandleConnecting
            )}
            isConnectableStart={false}
          />
        )}
        {children}
      </Flex>
    );
  }
);

NodeContainer.displayName = 'NodeContainer';
